/**
 * CommunityMessageMediaCard — the tappable media surface of an IMAGE or VIDEO
 * community message (the main-branch `messageTypeMedia` card). Renders a
 * "Processing" placeholder while the upload is in flight, otherwise the
 * transcoded thumbnail; a VIDEO also gets a centered play-arrow overlay. Tapping
 * a ready card opens the full-screen viewer (image) / playback (video).
 *
 * Flutter source of truth:
 *  - `community_message_item.dart:352-480` (`messageTypeMedia`, main branch,
 *    `replyInfo == null`): an 8px-rounded card; `imageWidth = cardWidth`,
 *    `imageHeight = imageWidth / aspectRatio` with the
 *    `DimensionConstants.videoPlayerAspectRatioPortrait` (= `9/16`) fallback.
 *  - `:417-432` — `isUploading` → a light box with centered `"Processing"`
 *    (shared by both media types).
 *  - `:433-452` — else the thumbnail (Flutter's pager renders the 480
 *    `thumbnail/` rendition, `relativeThumbnailPath`).
 *  - `:453-468` — VIDEO only: a centered `Icon(Icons.play_arrow, alpha .7,
 *    size 40)` overlay stacked over the thumbnail when NOT uploading. Ported here
 *    via the established web play-overlay pattern (see `VideoBubble.tsx:150-176`).
 *  - `:403-405` — tap no-ops while uploading.
 *
 * Scope: IMAGE and VIDEO main-branch cards. The media reply-preview branch
 * (`replyInfo != null`, `:352-384`) stays deferred under
 * `CommunityMessageReplyPreview` (its own play-overlay case with it).
 *
 * Community media is NOT E2EE (unlike chat direct media): the thumbnail is an
 * already-tokenized absolute download URL minted server-side by the transcode
 * cloud function (`MessageMediaInfoRemapper.toEntity` derives it via
 * `joinDefaultBucketUrl`), so this renders through `CachedImage` — the plaintext
 * cached-image path — NOT `EncryptedCachedImage`.
 *
 * Sizing (documented web deviation from Flutter's full `cardWidth`, which is a
 * phone-width assumption): mirrors `MediaBubbleFrame.tsx:27-52` — a viewport
 * width budget (`75vw` landscape / `50vw` portrait) folded together with a `50vh`
 * height cap into a single explicit `width` so the CSS `aspect-ratio` derives the
 * height exactly and `object-fit: cover` never crops. The card is centered by the
 * caller (Flutter wraps the card in `Center`).
 */

import React from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { getRatioPreservingMediaWidthCss } from '@/presentation/utils/mediaSizing';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface CommunityMessageMediaCardProps {
  /** The IMAGE or VIDEO message to render (uses `message.mediaInfo`). */
  message: CommunityChatMessageEntity;
  /**
   * Open the media surface for a READY card — the full-screen viewer (image) or
   * playback (video, wired in Task 15) — via the page's `handleMediaTap`
   * (`community_screen.dart:714-717`). Never fired while uploading (Flutter
   * `community_message_item.dart:403-405`).
   */
  onMediaTap: (message: CommunityChatMessageEntity) => void;
}

export const CommunityMessageMediaCard: React.FC<CommunityMessageMediaCardProps> = ({
  message,
  onMediaTap,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const mediaInfo = message.mediaInfo;
  const isUploading = mediaInfo?.isUploading ?? false;
  // VIDEO gets the play-arrow overlay (`community_message_item.dart:453-468`)
  // and doubles as the qa-tester's video/image discriminator below.
  const isVideo = message.type === CommunityChatMessageType.video;

  // Flutter fallback: `mediaInfo.aspectRatio ?? videoPlayerAspectRatioPortrait`
  // (= `9/16`) — `community_message_item.dart:368-369`.
  const resolvedAspectRatio =
    mediaInfo?.aspectRatio ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

  // Dual-cap, ratio-preserving width (shared with `MediaBubbleFrame` via
  // `getRatioPreservingMediaWidthCss`): width is the single explicit dimension,
  // clamped so the `aspect-ratio`-derived height (`width / aspectRatio`) can
  // never exceed `50vh`, so the media's real ratio always holds in both portrait
  // and landscape. See the helper for the full "why not max-height" rationale.
  const cardWidthCss = getRatioPreservingMediaWidthCss(resolvedAspectRatio);

  // Tap no-op while uploading (`community_message_item.dart:403-405`).
  const handleTap = (): void => {
    if (isUploading) {
      return;
    }
    onMediaTap(message);
  };

  const cardStyle: CSSProperties = {
    // Reset native button chrome.
    border: 'none',
    padding: 0,
    cursor: isUploading ? 'default' : 'pointer',
    // Ratio-preserving width capped on both axes (see `cardWidthCss` above); the
    // `aspect-ratio`-derived height is left to fall out so `object-fit: cover`
    // never crops.
    width: cardWidthCss,
    aspectRatio: String(resolvedAspectRatio),
    // 8px rounded card (`community_message_item.dart:397, 423-425`).
    borderRadius: responsive.borderRadius.base,
    overflow: 'hidden',
    // Mode-aware processing/placeholder surface — the `MediaBubbleFrame.tsx:61-72`
    // precedent, deviating from Flutter's fixed `AppColors.backgroundColorLight`
    // (a jarring white block in dark mode).
    backgroundColor: theme.secondary,
    display: 'block',
    position: 'relative',
  };

  const imageStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  return (
    <button
      type="button"
      {...qaAttr({
        id: `community-message-media-${message.id}`,
        status: isUploading ? 'uploading' : 'ready',
        // Video/image discriminator for the qa-tester (only emitted for video —
        // an image card has no `data-qa-value`). The `qaAttr` options object
        // omits the attribute entirely when `value` is `undefined`.
        value: isVideo ? 'video' : undefined,
      })}
      onClick={handleTap}
      style={cardStyle}
    >
      {isUploading || !mediaInfo?.thumbnail ? (
        // Uploading / not-yet-transcoded → the "Processing" box
        // (`community_message_item.dart:417-432`). The label is gated on
        // `isUploading`, matching the chat bubble's bare-surface fallback for a
        // present-but-still-loading thumbnail.
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploading && (
            <span
              style={{
                // Mode-aware label color so it reads on `theme.secondary` in
                // both themes (Flutter's literal white is invisible in light).
                color: theme.text.primary,
                fontSize: responsive.font.sm,
                textAlign: 'center',
              }}
            >
              {l10n.community.processing}
            </span>
          )}
        </div>
      ) : (
        // Ready → the transcoded 480 `thumbnail/` rendition (absolute, tokenized
        // download URL). Plaintext cached-image path (community media is NOT
        // E2EE), so `CachedImage`, not `EncryptedCachedImage`.
        <CachedImage src={mediaInfo.thumbnail} alt="" style={imageStyle} />
      )}

      {/* Video play-arrow overlay (`community_message_item.dart:453-468`),
          centered over the thumbnail — shown for a non-uploading VIDEO card,
          INCLUDING the codec-fallback no-thumbnail case (the empty
          `theme.secondary` surface above still reads as a playable video, mirror
          of `VideoBubble.tsx:150-157`). The round `theme.loadingOverlay` backing
          + `${theme.white}B3` (Flutter's 70%-alpha white) + `responsive.icon
          .xlarge` are the established web port of Flutter's 40px `play_arrow`.
          `pointerEvents: 'none'` keeps the whole-card tap (the button) intact. */}
      {isVideo && !isUploading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: responsive.borderRadius.round,
              backgroundColor: theme.loadingOverlay,
              padding: responsive.spacing.sm,
            }}
          >
            <AssetIcon
              path="/assets/vectors/play_filled.svg"
              width={responsive.icon.xlarge}
              height={responsive.icon.xlarge}
              color={`${theme.white}B3`}
            />
          </div>
        </div>
      )}
    </button>
  );
};
